import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faCalendarAlt, faPlus, faClock } from '@fortawesome/free-solid-svg-icons';
import {
  clinicsService,
  getAppointmentDurationMinutes,
  getAppointmentNotes,
  isAppointmentType,
  type Appointment,
  type AppointmentType,
  type AppointmentUpdatePayload,
  type ClinicRoom,
  type Patient,
} from '../../services/clinicsService';
import ClinicsNewPatientModal from './ClinicsNewPatientModal';
import { GhostSelect } from '../../components/ui/GhostSelect';
import { format, parseISO, isSameDay, addMinutes } from 'date-fns';
import { es } from 'date-fns/locale';
import { clinicSchedulesService, type ClinicScheduleLookup } from '../../services/clinicSchedulesService';
import { APPOINTMENT_STATUSES, getStatusColorDot, getStatusLabel } from '../../utils/appointmentStatus';
import { useNotifications } from '../../components/ui/notifications';

interface ClinicsNewAppointmentModalProps {
  onClose: () => void;
  onSuccess: () => void;
  preselectedPatientId?: number;
  initialDate?: Date;
  initialRoomId?: string;
  appointmentToEdit?: Appointment;
  rooms?: ClinicRoom[];
}

interface AppointmentFormState {
  patient_id: string;
  room_id: string;
  appointment_date: string;
  appointment_time: string;
  duration_minutes: number;
  appointment_type: AppointmentType;
  status: string;
  doctor_id: string;
  notes: string;
}

export default function ClinicsNewAppointmentModal({
  onClose,
  onSuccess,
  preselectedPatientId,
  initialDate,
  initialRoomId,
  appointmentToEdit,
  rooms = []
}: ClinicsNewAppointmentModalProps) {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const [patients, setPatients] = useState<Patient[]>([]);

  // Helper to extract date/time/duration from appointmentToEdit
  const getInitialFormState = (): AppointmentFormState => {
    let dateStr = '';
    let timeStr = '';
    let durationMins = 30;

    if (appointmentToEdit) {
      const start = parseISO(appointmentToEdit.start_time);
      dateStr = format(start, 'yyyy-MM-dd');
      timeStr = format(start, 'HH:mm');
      durationMins = getAppointmentDurationMinutes(appointmentToEdit);
    } else if (initialDate) {
      dateStr = format(initialDate, 'yyyy-MM-dd');
      timeStr = format(initialDate, 'HH:mm');
    }

    return {
      patient_id: appointmentToEdit ? String(appointmentToEdit.patient_id) : (preselectedPatientId ? String(preselectedPatientId) : ''),
      room_id: appointmentToEdit ? appointmentToEdit.room_id : (initialRoomId || ''),
      appointment_date: dateStr,
      appointment_time: timeStr,
      duration_minutes: durationMins,
      appointment_type: appointmentToEdit?.appointment_type || 'consultation',
      status: appointmentToEdit?.status || 'scheduled',
      doctor_id: appointmentToEdit?.doctor_id ? String(appointmentToEdit.doctor_id) : '',
      notes: appointmentToEdit ? getAppointmentNotes(appointmentToEdit) || '' : '',
    };
  };

  const [formData, setFormData] = useState(getInitialFormState());
  const [doctors, setDoctors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingPatients, setFetchingPatients] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewPatientModal, setShowNewPatientModal] = useState(false);

  // Right Panel State (Day View)
  const [daySchedule, setDaySchedule] = useState<ClinicScheduleLookup | null>(null);
  const [dayAppointments, setDayAppointments] = useState<Appointment[]>([]);
  const [loadingDayInfo, setLoadingDayInfo] = useState(false);

  const fetchAllPatientsAndDoctors = async () => {
    setFetchingPatients(true);
    try {
      const [patientsResp, doctorsResp] = await Promise.all([
        clinicsService.getPatients({ per_page: 100 }),
        clinicsService.getDoctors({ per_page: 100 })
      ]);
      setPatients(patientsResp.patients);
      setDoctors(doctorsResp.doctors);

      setFormData(prev => {
        const next = { ...prev };
        if (!next.room_id && rooms && rooms.length > 0) {
          next.room_id = rooms[0].id;
        }
        if (!next.doctor_id && doctorsResp.doctors.length > 0 && !appointmentToEdit) {
          next.doctor_id = String(doctorsResp.doctors[0].id);
        }
        if (!next.patient_id && !preselectedPatientId && patientsResp.patients.length === 1 && !appointmentToEdit) {
          next.patient_id = String(patientsResp.patients[0].id);
        }
        return next;
      });
    } catch (err) {
      console.error('Failed to fetch data', err);
    } finally {
      setFetchingPatients(false);
    }
  };

  useEffect(() => {
    fetchAllPatientsAndDoctors();
  }, []);

  useEffect(() => {
    if (!formData.room_id && rooms && rooms.length > 0) {
      setFormData(prev => ({ ...prev, room_id: rooms[0].id }));
    }
  }, [rooms]);

  // Fetch Day Info when date changes
  useEffect(() => {
    const fetchDayInfo = async () => {
      if (!formData.appointment_date) {
        setDaySchedule(null);
        setDayAppointments([]);
        return;
      }
      setLoadingDayInfo(true);
      try {
        const [schedLookup, appsResp] = await Promise.all([
          clinicSchedulesService.lookupSchedule(formData.appointment_date),
          clinicsService.getAppointments()
        ]);
        setDaySchedule(schedLookup);

        const dateObj = new Date(formData.appointment_date);
        const appsForDay = appsResp.appointments.filter(a => isSameDay(parseISO(a.start_time), dateObj));
        setDayAppointments(appsForDay);
      } catch (err) {
        console.error("Failed to load day info", err);
      } finally {
        setLoadingDayInfo(false);
      }
    };
    fetchDayInfo();
  }, [formData.appointment_date]);

  const handlePatientCreated = () => {
    setShowNewPatientModal(false);
    fetchAllPatientsAndDoctors();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.patient_id) {
      const message = t('clinics.modals.newAppointment.errors.noPatient');
      setError(message);
      notifications.warning('Revisa los datos', message);
      return;
    }
    if (!formData.room_id) {
      const message = t('clinics.modals.newAppointment.errors.noRoom');
      setError(message);
      notifications.warning('Revisa los datos', message);
      return;
    }
    if (!formData.appointment_date || !formData.appointment_time) {
      const message = t('clinics.modals.newAppointment.errors.noDateTime');
      setError(message);
      notifications.warning('Revisa la fecha y hora', message);
      return;
    }

    const dateStr = `${formData.appointment_date}T${formData.appointment_time}:00`;
    const startObj = new Date(dateStr);
    const endObj = addMinutes(startObj, formData.duration_minutes);

    const startTimeISO = startObj.toISOString();
    const endTimeISO = endObj.toISOString();

    setLoading(true);
    setError(null);
    try {
      const payload = {
        room_id: formData.room_id,
        start_time: startTimeISO,
        end_time: endTimeISO,
        appointment_type: formData.appointment_type,
        status: formData.status,
        doctor_id: formData.doctor_id ? Number(formData.doctor_id) : null,
        details: { notes: formData.notes || null },
      } satisfies AppointmentUpdatePayload;

      if (appointmentToEdit) {
        await clinicsService.updateAppointment(appointmentToEdit.id, payload);
        notifications.success('Cita actualizada', 'Los cambios se guardaron correctamente.');
      } else {
        await clinicsService.createAppointment({
          patient_id: Number(formData.patient_id),
          ...payload,
        });
        notifications.success('Cita creada', 'La cita se añadió correctamente.');
      }
      onSuccess();
    } catch (err: any) {
      let message: string;
      if (err.response?.status === 409) {
        message = t('clinics.modals.newAppointment.errors.conflict');
      } else {
        message = err.response?.data?.detail || t('clinics.modals.newAppointment.errors.generic');
      }
      setError(message);
      notifications.error('No se pudo guardar la cita', message);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.patient_id && formData.room_id && formData.appointment_date && formData.appointment_time && formData.duration_minutes && formData.appointment_type;

  const calculatedEndTime = (() => {
    if (!formData.appointment_time || !formData.duration_minutes) return null;
    const parts = formData.appointment_time.split(':');
    if (parts.length !== 2) return null;
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (isNaN(h) || isNaN(m)) return null;

    const totalMins = h * 60 + m + Number(formData.duration_minutes);
    const endH = Math.floor(totalMins / 60) % 24;
    const endM = totalMins % 60;
    return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
  })();

  const renderMiniCalendar = () => {
    if (!formData.appointment_date) return (
      <div data-ui="clinics-new-appointment-modal-region-001" className="flex-1 flex items-center justify-center text-white/40 p-6 text-center">
        {t('clinics.modals.newAppointment.calendar.selectDate')}
      </div>
    );

    if (loadingDayInfo) return (
      <div data-ui="clinics-new-appointment-modal-region-002" className="flex-1 flex items-center justify-center p-6">
        <div data-ui="clinics-new-appointment-modal-region-003" className="w-8 h-8 border-2 border-white/50 border-t-white rounded-full animate-spin"></div>
      </div>
    );

    const START_HOUR = 7;
    const END_HOUR = 21;
    const timeSlots = [];
    for (let h = START_HOUR; h < END_HOUR; h++) {
      timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    }

    const selectedRoom = rooms.find(r => r.id === formData.room_id);
    const miniRoomColor = selectedRoom?.color_code || '#3b82f6';
    const miniAlphaMajor = miniRoomColor.startsWith('#') && miniRoomColor.length === 7 ? `${miniRoomColor}33` : `color-mix(in srgb, ${miniRoomColor} 20%, transparent)`;
    const miniAlphaMinor = miniRoomColor.startsWith('#') && miniRoomColor.length === 7 ? `${miniRoomColor}12` : `color-mix(in srgb, ${miniRoomColor} 7%, transparent)`;

    const miniBlueprintStyle: React.CSSProperties = {
      backgroundImage: `
        linear-gradient(to right, ${miniAlphaMajor} 1px, transparent 1px),
        linear-gradient(to bottom, ${miniAlphaMajor} 1px, transparent 1px),
        linear-gradient(to right, ${miniAlphaMinor} 1px, transparent 1px),
        linear-gradient(to bottom, ${miniAlphaMinor} 1px, transparent 1px)
      `,
      backgroundSize: '48px 48px, 48px 48px, 16px 16px, 16px 16px'
    };

    return (
      <div data-ui="clinics-new-appointment-modal-region-004" className="flex-1 flex flex-col overflow-hidden bg-black/20 relative">
        <div data-ui="clinics-new-appointment-modal-region-005" className="p-3 bg-black/20 border-b border-white/10 text-center sticky top-0 z-20">
          <h3 className="text-sm font-bold text-white capitalize">
            {format(new Date(formData.appointment_date), 'EEEE, d MMM yyyy', { locale: es })}
          </h3>
          {daySchedule?.is_holiday && (
            <div data-ui="clinics-new-appointment-modal-region-006" className="text-xs text-red-300 mt-1">{daySchedule.holiday_description || t('clinics.modals.newAppointment.calendar.holiday')}</div>
          )}
          {!daySchedule?.is_open && (
            <div data-ui="clinics-new-appointment-modal-region-007" className="text-xs text-orange-300 mt-1">{t('clinics.modals.newAppointment.calendar.closed')}</div>
          )}
        </div>
        <div data-ui="clinics-new-appointment-modal-region-008" className="flex-1 overflow-y-auto relative custom-scrollbar" style={miniBlueprintStyle}>
          {timeSlots.map(time => {
            const [h, m] = time.split(':').map(Number);
            const slotTime = h * 60 + m;
            let available = false;
            if (daySchedule?.is_open) {
              available = daySchedule.schedules.some(s => {
                const [oh, om] = s.start_time.split(':').map(Number);
                const [ch, cm] = s.end_time.split(':').map(Number);
                return slotTime >= (oh * 60 + om) && slotTime < (ch * 60 + cm);
              });
            }

            return (
              <div data-ui="clinics-new-appointment-modal-region-009" key={time} className={`h-12 border-b border-white/5 flex ${available ? 'bg-blue-900/10' : 'bg-black/30'}`}>
                <div data-ui="clinics-new-appointment-modal-region-010" className="w-12 flex-shrink-0 border-r border-white/10 text-xs text-white/40 flex items-center justify-center bg-black/20">
                  {time}
                </div>
                <div data-ui="clinics-new-appointment-modal-region-011" className="flex-1 relative">
                </div>
              </div>
            );
          })}

          <div data-ui="clinics-new-appointment-modal-region-012" className="absolute top-0 bottom-0 left-12 right-0 pointer-events-none">
            {dayAppointments.filter(a => a.status !== 'cancelled' && (!appointmentToEdit || a.id !== appointmentToEdit.id)).map(app => {
              if (formData.room_id && app.room_id !== formData.room_id) return null; // Only show apps for the selected room

              const start = parseISO(app.start_time);
              const end = parseISO(app.end_time);
              const startMins = start.getHours() * 60 + start.getMinutes();
              const durationMins = (end.getTime() - start.getTime()) / (1000 * 60);
              const offsetMins = startMins - (START_HOUR * 60);

              if (offsetMins < 0) return null;

              const topPercentage = (offsetMins / ((END_HOUR - START_HOUR) * 60)) * 100;
              const heightPercentage = (durationMins / ((END_HOUR - START_HOUR) * 60)) * 100;

              return (
                <div data-ui="clinics-new-appointment-modal-region-013"
                  key={app.id}
                  className="absolute left-1 right-2 rounded border border-white/20 bg-white/10 text-white text-[10px] p-1 overflow-hidden backdrop-blur-sm"
                  style={{ top: `${topPercentage}%`, height: `${heightPercentage}%` }}
                >
                  <span className="font-bold">{format(start, 'HH:mm')}</span> - {app.patient_name || `Pac. #${app.patient_id}`}
                </div>
              );
            })}

            {formData.appointment_time && formData.duration_minutes && formData.room_id && (
              <div data-ui="clinics-new-appointment-modal-region-014"
                className="absolute left-1 right-2 rounded border-2 border-dashed border-blue-400 bg-blue-500/20 text-blue-100 text-[10px] p-1 overflow-hidden z-10 flex flex-col"
                style={{
                  top: `${((Number(formData.appointment_time.split(':')[0]) * 60 + Number(formData.appointment_time.split(':')[1]) - (START_HOUR * 60)) / ((END_HOUR - START_HOUR) * 60)) * 100}%`,
                  height: `${(formData.duration_minutes / ((END_HOUR - START_HOUR) * 60)) * 100}%`
                }}
              >
                <b>{appointmentToEdit ? t('clinics.modals.newAppointment.calendar.editing') : t('clinics.modals.newAppointment.calendar.new')}</b>
                <span className="truncate opacity-90 leading-tight">
                  {patients.find(p => String(p.id) === formData.patient_id)?.full_name || ''}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-ui="clinics-new-appointment-modal-region-015" className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div data-ui="clinics-new-appointment-modal-region-016" className="bg-black/30 backdrop-filter backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div data-ui="clinics-new-appointment-modal-region-017" className="px-6 py-4 border-b border-white/20 flex justify-between items-center bg-black/30">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-400" />
            {appointmentToEdit ? t('clinics.modals.newAppointment.editTitle') : t('clinics.modals.newAppointment.title')}
          </h2>
          <button
            data-ui="clinics-new-appointment-modal-close"
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div data-ui="clinics-new-appointment-modal-region-018" className="flex flex-col md:flex-row flex-1 overflow-hidden bg-black-800/50">

          <div data-ui="clinics-new-appointment-modal-region-019" className="w-full md:w-[60%] p-6 overflow-y-auto custom-scrollbar flex flex-col border-r border-white/10">
            {error && (
              <div data-ui="clinics-new-appointment-modal-region-020" className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
                {error}
              </div>
            )}

            <form id="new-appointment-form" onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 flex-1">
              <div data-ui="clinics-new-appointment-modal-region-021" className="grid gap-1">
                <span className="text-xs text-white/80">{t('clinics.modals.newAppointment.patient')}</span>
                <div data-ui="clinics-new-appointment-modal-region-022" className="flex gap-2 items-center">
                  <GhostSelect
                    value={formData.patient_id}
                    onChange={(val: string) => setFormData({ ...formData, patient_id: val })}
                    disabled={!!preselectedPatientId || fetchingPatients}
                    className="flex-1"
                    options={[
                      { value: "", label: fetchingPatients ? t('clinics.modals.newAppointment.loadingPatients') : t('clinics.modals.newAppointment.selectPatient') },
                      ...patients.map(p => ({ value: String(p.id), label: p.full_name }))
                    ]}
                  />
                  {!preselectedPatientId && (
                    <button
                      type="button"
                      onClick={() => setShowNewPatientModal(true)}
                      className="w-8 h-8 shrink-0 text-sm bg-black/20 border border-white/20 hover:bg-white/10 text-white flex items-center justify-center rounded-md transition-colors self-center"
                      title={t('clinics.modals.newAppointment.createPatient')}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  )}
                </div>
              </div>

              <div data-ui="clinics-new-appointment-modal-region-023" className="grid grid-cols-2 gap-4">
                <label className="grid gap-1">
                  <span className="text-xs text-white/80">{t('clinics.modals.newAppointment.date')}</span>
                  <input
                    type="date"
                    required
                    className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
                    value={formData.appointment_date}
                    onChange={e => setFormData({ ...formData, appointment_date: e.target.value })}
                  />
                </label>

                <label className="grid gap-1">
                  <span className="text-xs text-white/80">{t('clinics.modals.newAppointment.time')}</span>
                  <input
                    type="time"
                    required
                    className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
                    value={formData.appointment_time}
                    onChange={e => setFormData({ ...formData, appointment_time: e.target.value })}
                  />
                </label>
              </div>

              <div data-ui="clinics-new-appointment-modal-region-024" className="grid grid-cols-2 gap-4">
                <label className="grid gap-1">
                  <div data-ui="clinics-new-appointment-modal-region-025" className="flex items-center justify-between">
                    <span className="text-xs text-white/80">{t('clinics.modals.newAppointment.duration')}</span>
                    {calculatedEndTime && (
                      <span className="text-[11px] text-emerald-400 font-bold bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded flex items-center gap-1 shadow-sm animate-fade-in" title="Hora de finalización estimada">
                        <FontAwesomeIcon icon={faClock} className="text-[10px]" />
                        Fin: {calculatedEndTime}
                      </span>
                    )}
                  </div>
                  <input
                    type="number"
                    required
                    min="5"
                    max="480"
                    step="5"
                    className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.duration_minutes}
                    onChange={e => setFormData({ ...formData, duration_minutes: Number(e.target.value) })}
                  />
                  {calculatedEndTime && (
                    <span className="text-[11px] text-white/60">
                      Finaliza a las <strong className="text-emerald-400 font-semibold">{calculatedEndTime} hs</strong>
                    </span>
                  )}
                </label>

                <label className="grid gap-1">
                  <span className="text-xs text-white/80">{t('clinics.modals.newAppointment.room')}</span>
                  <GhostSelect
                    value={formData.room_id}
                    onChange={(val: string) => setFormData({ ...formData, room_id: val })}
                    className="w-full"
                    options={[
                      { value: "", label: t('clinics.modals.newAppointment.selectRoom'), textLabel: t('clinics.modals.newAppointment.selectRoom') },
                      ...rooms.map(r => ({
                        value: r.id,
                        label: (
                          <div data-ui="clinics-new-appointment-modal-region-026" className="flex items-center gap-2">
                            <div data-ui="clinics-new-appointment-modal-region-027" className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: r.color_code || '#808080' }} />
                            <span className={!r.is_active ? 'opacity-50 line-through' : ''}>
                              {r.name} {!r.is_active && <span className="text-[10px] ml-1 opacity-70">(inactiva)</span>}
                            </span>
                          </div>
                        ),
                        textLabel: `${r.name} ${!r.is_active ? '(inactiva)' : ''}`
                      }))
                    ]}
                  />
                </label>
              </div>

              <div data-ui="clinics-new-appointment-modal-region-028" className="grid grid-cols-2 gap-4">
                <label className="grid gap-1">
                  <span className="text-xs text-white/80">{t('clinics.modals.newAppointment.type')}</span>
                  <GhostSelect
                    value={formData.appointment_type}
                    onChange={(val: string) => {
                      if (isAppointmentType(val)) setFormData({ ...formData, appointment_type: val });
                    }}
                    className="w-full"
                    options={[
                      { value: "consultation", label: t('clinics.modals.newAppointment.types.consultation') },
                      { value: "follow-up", label: t('clinics.modals.newAppointment.types.follow-up') },
                      { value: "checkup", label: t('clinics.modals.newAppointment.types.checkup') },
                      { value: "emergency", label: t('clinics.modals.newAppointment.types.emergency') },
                    ]}
                  />
                </label>

                <label className="grid gap-1">
                  <span className="text-xs text-white/80">{t('clinics.modals.newAppointment.status')}</span>
                  <GhostSelect
                    value={formData.status}
                    onChange={(val: string) => setFormData({ ...formData, status: val })}
                    className="w-full"
                    options={APPOINTMENT_STATUSES.map(status => ({
                      value: status,
                      label: (
                        <div data-ui="clinics-new-appointment-modal-region-029" className="flex items-center gap-2">
                          <div data-ui="clinics-new-appointment-modal-region-030" className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusColorDot(status) }} />
                          {getStatusLabel(status, t)}
                        </div>
                      )
                    }))}
                  />
                </label>
              </div>

              <div data-ui="clinics-new-appointment-modal-region-031" className="grid gap-1">
                <label className="grid gap-1">
                  <span className="text-xs text-white/80">{t('clinics.modals.newAppointment.doctor')}</span>
                  <GhostSelect
                    value={formData.doctor_id}
                    onChange={(val: string) => setFormData({ ...formData, doctor_id: val })}
                    className="w-full"
                    options={[
                      { value: "", label: t('clinics.modals.newAppointment.selectDoctor') },
                      ...doctors.map(d => ({ value: String(d.id), label: `${d.name} ${d.specialty ? `(${d.specialty})` : ''}` }))
                    ]}
                  />
                </label>
              </div>

              <label className="grid gap-1 flex-1">
                <span className="text-xs text-white/80">{t('clinics.modals.newAppointment.notes')}</span>
                <textarea
                  className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 h-full min-h-[80px]"
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={t('clinics.modals.newAppointment.notesPlaceholder')}
                />
              </label>
            </form>
          </div>

          <div data-ui="clinics-new-appointment-modal-region-032" className="w-full md:w-[40%] flex flex-col border-t md:border-t-0 md:border-l border-white/10 overflow-hidden">
            {renderMiniCalendar()}
          </div>
        </div>

        <div data-ui="clinics-new-appointment-modal-region-033" className="px-6 py-4 border-t border-white/20 bg-black/30 flex justify-end gap-3 z-10">
          <button
            data-ui="clinics-new-appointment-modal-cancel"
            type="button"
            onClick={onClose}
            className="btn-taratic btn-ghost-dark px-4 py-2 border"
          >
            {t('clinics.modals.newAppointment.cancel')}
          </button>
          <button
            type="submit"
            form="new-appointment-form"
            disabled={loading || !isFormValid}
            className="btn-taratic btn-green px-4 py-2 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div data-ui="clinics-new-appointment-modal-region-034" className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin"></div>
            ) : (
              <FontAwesomeIcon icon={faSave} />
            )}
            {appointmentToEdit ? t('clinics.modals.newAppointment.update') : t('clinics.modals.newAppointment.save')}
          </button>
        </div>
      </div>

      {showNewPatientModal && (
        <ClinicsNewPatientModal
          onClose={() => setShowNewPatientModal(false)}
          onSuccess={handlePatientCreated}
        />
      )}
    </div>
  );
}
